import { useState, useEffect, useCallback } from 'react';
import { ProductList } from './components/ProductList';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { ControlsBar } from './components/ControlsBar';
import { Modal } from './components/Modal';
import { SettingsModal } from './components/SettingsModal';
import { HistoryModal } from './components/HistoryModal';
import { AboutModal } from './components/AboutModal';
import { AddProductModal } from './components/AddProductModal';
import { EditProductModal } from './components/EditProductModal';
import { DeleteConfirmModal } from './components/DeleteConfirmModal';
import { Login } from './components/Login';
import { Product } from './types';
import { getProducts, checkAllPrices, getStats, createProduct, updateProduct, deleteProduct, checkAuth, logout } from './services/api';

function App() {
  const [products, setProducts] = useState<Product[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [stats, setStats] = useState({ totalProducts: 0, totalPrices: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [authRequired, setAuthRequired] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<Product | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<Product | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const loadProducts = useCallback(async () => {
    try {
      const data = await getProducts();
      setProducts(data);
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const data = await getStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  const refreshPrices = async () => {
    setIsChecking(true);
    try {
      await checkAllPrices();
      await loadProducts();
      await loadStats();
    } catch (error) {
      console.error('Failed to refresh prices:', error);
      alert('Erreur lors du rafraîchissement des prix');
    } finally {
      setIsChecking(false);
    }
  };

  const handleAddProduct = async (product: Omit<Product, 'id' | 'created_at' | 'updated_at'>) => {
    await createProduct(product);
    await loadProducts();
    setShowAddModal(false);
  };

  const handleEditProduct = async (product: Product) => {
    await updateProduct(product.id, product);
    await loadProducts();
    setShowEditModal(null);
  };

  const handleDeleteProduct = async (productId: number) => {
    await deleteProduct(productId);
    await loadProducts();
    setShowDeleteModal(null);
  };

  useEffect(() => {
    const init = async () => {
      try {
        const auth = await checkAuth();
        setAuthRequired(auth.authRequired);
        setAuthenticated(auth.authenticated);
      } catch {
        // If the auth check itself fails, fail safe by not exposing data
        setAuthRequired(true);
        setAuthenticated(false);
      } finally {
        setAuthChecked(true);
      }
    };
    init();
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => setAuthenticated(false);
    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, []);

  useEffect(() => {
    if (authRequired && !authenticated) return;
    const init = async () => {
      await Promise.all([loadProducts(), loadStats()]);
      setIsLoading(false);
    };
    init();
  }, [authRequired, authenticated, loadProducts, loadStats]);

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  if (authRequired && !authenticated) {
    return <Login onSuccess={() => setAuthenticated(true)} />;
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white flex">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onHistoryClick={() => setShowHistoryModal(true)}
        onSettingsClick={() => setShowSettingsModal(true)}
        onAboutClick={() => setShowAboutModal(true)}
        onLogoutClick={authRequired ? async () => {
          await logout();
          setAuthenticated(false);
        } : undefined}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <Header
          onAddClick={() => setShowAddModal(true)}
          onRefreshClick={refreshPrices}
          onMenuClick={() => setSidebarOpen(true)}
          isChecking={isChecking}
        />

        <main className="flex-1 p-6 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
            </div>
          ) : (
            <ProductList
              products={products}
              onEdit={setShowEditModal}
              onDelete={setShowDeleteModal}
            />
          )}
        </main>

        <ControlsBar totalProducts={stats.totalProducts} />
      </div>

      {/* Modals */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Ajouter un produit" size="lg">
        <AddProductModal onSubmit={handleAddProduct} onCancel={() => setShowAddModal(false)} />
      </Modal>

      <Modal isOpen={!!showEditModal} onClose={() => setShowEditModal(null)} title="Modifier le produit" size="lg">
        {showEditModal && <EditProductModal product={showEditModal} onSubmit={handleEditProduct} onCancel={() => setShowEditModal(null)} />}
      </Modal>

      <Modal isOpen={!!showDeleteModal} onClose={() => setShowDeleteModal(null)} title="Confirmer la suppression" size="md">
        {showDeleteModal && <DeleteConfirmModal product={showDeleteModal} onConfirm={handleDeleteProduct} onCancel={() => setShowDeleteModal(null)} />}
      </Modal>

      <Modal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)} title="Historique des prix" size="xl">
        <HistoryModal />
      </Modal>

      <Modal isOpen={showSettingsModal} onClose={() => setShowSettingsModal(false)} title="Paramètres" size="lg">
        <SettingsModal onClose={() => setShowSettingsModal(false)} />
      </Modal>

      <Modal isOpen={showAboutModal} onClose={() => setShowAboutModal(false)} title="À propos" size="md">
        <AboutModal onClose={() => setShowAboutModal(false)} />
      </Modal>
    </div>
  );
}

export default App;